import { jsx, jsxs } from "react/jsx-runtime";
import React__default, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { AnalyticsId } from "../../constants.js";
import { useExpand } from "../../hooks/useExpand.js";
import { useHover } from "../../hooks/useHover.js";
import { useUuid } from "../../hooks/useUuid.js";
import { mergeRefs } from "../../utils/refs.js";
import { isElementInViewport } from "../../utils/viewport.js";
import ChevronDown from "../Icons/ChevronDown.js";
import ChevronUp from "../Icons/ChevronUp.js";
import { r as renderListHeader } from "../../ListHeader.js";
import expanderListStyles from "./styles.module.scss";
const Expander = React__default.forwardRef((props, ref) => {
  const {
    id,
    children,
    padding = true,
    color = "neutral",
    className = "",
    icon,
    large = false,
    title,
    titleHtmlMarkup = "span",
    expanded = false,
    testId,
    handleExpanderClick,
    onExpand,
    renderChildrenWhenClosed,
    variant = "line",
    zIndex
  } = props;
  const [isExpanded] = useExpand(expanded, onExpand);
  const expanderRef = useRef(null);
  const triggerRef = useRef(null);
  const { isHovered } = useHover(triggerRef);
  const isFill = variant === "fill";
  const isFillNegative = variant === "fill-negative";
  const isOutline = variant === "outline";
  const isLine = variant === "line";
  const itemClasses = classNames(className, expanderListStyles["expander-list__item"], {
    [expanderListStyles[`expander-list__item--fill`]]: isFill,
    [expanderListStyles[`expander-list__item--fill--${color}`]]: isFill,
    [expanderListStyles[`expander-list__item--fill-negative`]]: isFillNegative,
    [expanderListStyles["expander-list__item--outline"]]: isOutline,
    [expanderListStyles[`expander-list__item--outline--${color}`]]: isOutline,
    [expanderListStyles["expander-list__item--line"]]: isLine,
    [expanderListStyles[`expander-list__item--line--${color}`]]: isLine
  });
  const expanderClasses = classNames(expanderListStyles["expander-list-link"], expanderListStyles[`expander-list-link--${color}`], {
    [expanderListStyles[`expander-list-link--fill`]]: isFill,
    [expanderListStyles[`expander-list-link--fill--${color}`]]: isFill,
    [expanderListStyles[`expander-list-link--fill-negative`]]: isFillNegative,
    [expanderListStyles["expander-list-link--outline"]]: isOutline,
    [expanderListStyles[`expander-list-link--outline--${color}`]]: isOutline,
    [expanderListStyles[`expander-list-link--line--${color}`]]: isLine,
    [expanderListStyles["expander-list-link--closed"]]: !isExpanded,
    [expanderListStyles["expander-list-link--open"]]: isExpanded,
    [expanderListStyles["expander-list-link--large"]]: large
  });
  const renderContent = () => {
    if (!renderChildrenWhenClosed && !isExpanded) {
      return null;
    }
    const mainContentClasses = classNames(
      expanderListStyles["expander-list-link__main-content"],
      isExpanded && expanderListStyles["expander-list-link__main-content--expanded"],
      padding ? expanderListStyles["expander-list-link__main-content--padding"] : ""
    );
    return /* @__PURE__ */ jsx("div", { className: mainContentClasses, children });
  };
  return /* @__PURE__ */ jsxs("li", { className: itemClasses, ref: mergeRefs([ref, expanderRef]), children: [
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        id,
        onClick: handleExpanderClick,
        "data-testid": testId,
        "data-analyticsid": AnalyticsId.ExpanderListExpander,
        className: expanderClasses,
        ref: triggerRef,
        "aria-expanded": isExpanded,
        style: {
          zIndex: zIndex ?? void 0
        },
        children: renderListHeader(title, titleHtmlMarkup, isHovered, large ? "large" : "medium", isExpanded ? ChevronUp : ChevronDown, icon)
      }
    ),
    renderContent()
  ] });
});
const isExpanderComponent = (element) => React__default.isValidElement(element) && element.type === Expander;
const ExpanderList = React__default.forwardRef((props, ref) => {
  const {
    children,
    childPadding = true,
    large,
    renderChildrenWhenClosed = false,
    color,
    className = "",
    accordion = false,
    testId,
    variant,
    zIndex
  } = props;
  const [activeExpander, setActiveExpander] = useState();
  const [latestExpander, setLatestExpander] = useState();
  const uuid = useUuid();
  const expanderListClasses = classNames(expanderListStyles["expander-list"], className);
  function handleExpanderClick(event, id) {
    setActiveExpander((prevState) => accordion ? { [id]: !(prevState == null ? void 0 : prevState[id]) } : { ...prevState, [id]: !(prevState == null ? void 0 : prevState[id]) });
    setLatestExpander(event.currentTarget);
  }
  const getExpanderId = (index) => `${uuid}-${index}`;
  useEffect(() => {
    if (accordion && latestExpander && !isElementInViewport(latestExpander)) {
      latestExpander.scrollIntoView();
    }
  }, [accordion, latestExpander]);
  useEffect(() => {
    var _a;
    const newActiveExpander = (_a = React__default.Children.map(children, (child) => {
      if (isExpanderComponent(child)) {
        return child;
      }
    })) == null ? void 0 : _a.reduce((acc, child, index) => {
      if (typeof child.props.expanded !== "undefined") {
        acc[getExpanderId(index)] = child.props.expanded;
      }
      return acc;
    }, {});
    setActiveExpander({ ...activeExpander, ...newActiveExpander });
  }, [children]);
  return /* @__PURE__ */ jsx("ul", { className: expanderListClasses, ref, "data-testid": testId, "data-analyticsid": AnalyticsId.ExpanderList, children: React__default.Children.map(children, (child, index) => {
    if (isExpanderComponent(child)) {
      const id = getExpanderId(index);
      const expanded = activeExpander == null ? void 0 : activeExpander[id];
      return React__default.cloneElement(child, {
        id,
        key: index,
        expanded,
        padding: childPadding,
        color,
        large,
        "aria-expanded": expanded,
        className: expanderListStyles["expander-list__item"],
        handleExpanderClick: (event) => handleExpanderClick(event, `${uuid}-${index}`),
        renderChildrenWhenClosed,
        variant,
        zIndex
      });
    }
    return child;
  }) });
});
ExpanderList.displayName = "ExpanderList";
ExpanderList.Expander = Expander;
Expander.displayName = "ExpanderList.Expander";
export {
  ExpanderList,
  ExpanderList as default
};
//# sourceMappingURL=index.js.map
